/**
 * vx_scale1 레인 E — (src/lib/site-image/scale-caption.ts · 서버 쪽 별칭 src/lib/imagegen/scale/caption.ts) 규모 사실값 → 노출 문구(alt·caption·사실 배지) «단일 출처». 순수 TS(DB·네트워크·env 0).
 *
 * 규칙(instr/cl/vx_scale1 §E · v2 §J · v3-5):
 *   - 사실값이 있을 때: 「{단지명} {dong}개동·최고 {floor}층·총 {complex_total}세대 기준 AI 상상도 — 실제와 다를 수 있음」.
 *   - 세대는 단지 총세대(units = complex_total)와 이번 공급(supplyUnits)을 구분한다: 「총 N세대」 · 둘 다면 「총 N세대(이번 M세대 공급)」 ·
 *     공급만 알면 「이번 M세대 공급」 만. apt_sites.total_units(공급과 섞인 값)는 쓰지 않는다(v3 실측).
 *   - 모르는 값은 «그 조각만» 뺀다. 셋 다 모르면 null → 호출부는 종전 문구(site_images.caption_ko 등)를 그대로 쓴다.
 *   - ⛔ 추정값(estimated=true · confidence='low' · source='estimate')은 alt·caption·배지에 «절대» 넣지 않는다.
 *   - 「AI 상상도」 표지는 언제나 남는다. 「조감도」 는 쓰지 않는다(공식 이미지 전용 — cl-core/alt).
 * 화면(목록 카드·상세 히어로·갤러리·og alt·JSON-LD caption)은 site-image 리졸버가 이 파일을 «한 번» 거쳐 낸다.
 * 블로그·디자인 시각자료(vx4 facts)의 동 수·최고층도 `confirmedSpecValues` 로만 넣는다(추정 숫자 유입 0).
 */
import type { ScaleConfidence, ScaleFact, SiteScale, ScaleTier, SiteBuildingType } from '@/lib/imagegen/scale/types';
/**
 * 컷 한글(alt·figcaption 용) — imagegen/pure SHOT_LABEL_KO 의 하이픈→띄어쓰기 값과 같다(시험이 잠근다).
 * 이 파일이 imagegen/** 밖(site-image)에 있는 까닭: 리졸버(site-image/pure)가 클라이언트 그래프(KdTable 등)에 닿는다 — G-VX9(imagegen 의 클라이언트 유입 0).
 *   그래서 imagegen/pure 도 import 하지 않는다(의존은 types 의 type 전용 import 하나).
 */
export const SCALE_SHOT_LABEL: Readonly<Record<string, string>> = Object.freeze({
  S1_aerial_day: '주간 항공',
  S2_aerial_golden: '노을 항공',
  S3_eye_level: '단지 입구',
  S4_night: '야경',
});
const isShot = (x: unknown): x is string => typeof x === 'string' && Object.prototype.hasOwnProperty.call(SCALE_SHOT_LABEL, x);
const shotLabelText = (x: string): string => SCALE_SHOT_LABEL[x];

/** 확인된(노출해도 되는) 규모 값 — 모르는·추정 값은 null. */
export interface ConfirmedScaleFacts {
  /** 단지 총세대(complex_total) */
  units: number | null;
  dong: number | null;
  maxFloor: number | null;
  /** 이번 공급 세대(v3-5 · 계약 예고 필드 supplyUnits) — 규모 판단에는 쓰지 않고 표기만 구분한다. */
  supply: number | null;
}

export const NO_SCALE_FACTS: ConfirmedScaleFacts = Object.freeze({ units: null, dong: null, maxFloor: null, supply: null }) as ConfirmedScaleFacts;

/** 계약 예고(vs1-a): SiteScale.supplyUnits — 아직 types.ts 에 없을 수 있어 느슨하게 읽는다. */
export type SiteScaleLike = SiteScale & { supplyUnits?: ScaleFact<number> | null };

/** 고지 꼬리 — 「AI 상상도」 표지 + 실제와 다를 수 있음. */
export const SCALE_CAPTION_TAIL = 'AI 상상도 — 실제와 다를 수 있음';

const RANGE = {
  units: [1, 30000],
  dong: [1, 80],
  maxFloor: [2, 120],
} as const;

function intIn(v: unknown, lo: number, hi: number): number | null {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < lo || n > hi) return null;
  return n;
}

/** ScaleFact 하나가 «노출 가능한 사실» 인가 — 추정·저신뢰·출처 'estimate' 는 아니다. */
export function isConfirmedFact(f: ScaleFact<unknown> | null | undefined): boolean {
  if (!f || f.value === null || f.value === undefined) return false;
  if (f.estimated === true) return false;
  if (f.confidence !== 'high' && f.confidence !== 'medium') return false;
  if (String(f.source ?? '').trim().toLowerCase() === 'estimate') return false;
  return true;
}

function confirmedInt(f: ScaleFact<number> | null | undefined, k: keyof typeof RANGE): number | null {
  if (!isConfirmedFact(f)) return null;
  const [lo, hi] = RANGE[k];
  return intIn(f!.value, lo, hi);
}

/** SiteScale → 확인된 값만. scale 이 없으면 전부 null. */
export function confirmedScaleFacts(scale: SiteScale | null | undefined): ConfirmedScaleFacts {
  if (!scale) return NO_SCALE_FACTS;
  return {
    units: confirmedInt(scale.units, 'units'),
    dong: confirmedInt(scale.dong, 'dong'),
    maxFloor: confirmedInt(scale.maxFloor, 'maxFloor'),
    supply: confirmedInt((scale as SiteScaleLike).supplyUnits ?? null, 'units'),
  };
}

/** 세대 조각 — 「총 N세대」 · 「총 N세대(이번 M세대 공급)」 · 「이번 M세대 공급」 · 없으면 null. */
export function unitsPart(f: ConfirmedScaleFacts): string | null {
  if (f.units !== null) return f.supply !== null && f.supply !== f.units ? `총 ${fmt(f.units)}세대(이번 ${fmt(f.supply)}세대 공급)` : `총 ${fmt(f.units)}세대`;
  return f.supply !== null ? `이번 ${fmt(f.supply)}세대 공급` : null;
}

function fmt(n: number): string { return n.toLocaleString('ko-KR'); }

/** 「3개동·최고 15층·총 1,234세대」(sep 기본 '·'). 확인된 조각만 · 없으면 ''. */
export function scaleFactsPhrase(f: ConfirmedScaleFacts | null | undefined, sep = '·'): string {
  if (!f) return '';
  const parts: string[] = [];
  if (f.dong !== null) parts.push(`${fmt(f.dong)}개동`);
  if (f.maxFloor !== null) parts.push(`최고 ${fmt(f.maxFloor)}층`);
  const u = unitsPart(f);
  if (u) parts.push(u);
  return parts.join(sep);
}

/**
 * v2 §J 사실 배지 — 「3개동 · 최고 15층」(확인된 동·층만). 없으면 null.
 * hotfix(CL5): 세대 조각은 넣지 않는다 — 세대는 리드·핵심 정보 표·FAQ·카드 meta 가 이미 말해 배지까지 더하면 같은 수치가 4회 이상이 된다
 * (운영 report/cl/vx_scale1/post_u1 · 상세 12/40 · 허브 7/11). 세대 구분 표기(총/이번 공급)는 캡션·alt(scaleCaptionText·scaleAltText)가 맡는다.
 */
export function scaleBadgeText(scale: SiteScale | null | undefined): string | null {
  const f = confirmedScaleFacts(scale);
  const p = scaleFactsPhrase(f ? { ...f, units: null, supply: null } : f, ' · ');
  return p || null;
}

const clean = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

/**
 * 캡션 — 「{단지명} {사실} 기준 AI 상상도 — 실제와 다를 수 있음」. 이름이 없거나 확인된 사실이 없으면 null(종전 문구 유지).
 */
export function scaleCaptionText(name: string | null | undefined, scale: SiteScale | null | undefined): string | null {
  const n = clean(name);
  const phrase = scaleFactsPhrase(confirmedScaleFacts(scale));
  if (!n || !phrase) return null;
  return `${n} ${phrase} 기준 ${SCALE_CAPTION_TAIL}`;
}

/**
 * alt — 캡션과 같은 문장에 컷 이름을 괄호로 더한다(같은 현장 4컷 alt 중복 0 — rowsToShots 가 alt 로 중복을 거른다).
 *   「{단지명} {사실} 기준 AI 상상도(주간 항공) — 실제와 다를 수 있음」. 컷을 모르면 괄호 없이. 조건은 캡션과 같다.
 */
export function scaleAltText(name: string | null | undefined, scale: SiteScale | null | undefined, shot?: string | null): string | null {
  const n = clean(name);
  const phrase = scaleFactsPhrase(confirmedScaleFacts(scale));
  if (!n || !phrase) return null;
  const label = shot && isShot(shot) ? `(${shotLabelText(shot)})` : '';
  return `${n} ${phrase} 기준 AI 상상도${label} — 실제와 다를 수 있음`;
}

/**
 * site_images.caption_ko(작업자 captionKo: 「{표시명} {컷 한글} 상상도 · AI 생성 이미지로 실제와 다를 수 있음」)에서 표시명을 되읽는다.
 *   리졸버에 현장 이름이 안 실려 온 호출부(id 만)용. 꼴이 다르면 null(덮어쓰지 않는다).
 */
export function nameFromStoredCaption(caption: string | null | undefined, shot: string | null | undefined): string | null {
  const c = clean(caption);
  if (!c || !shot || !isShot(shot)) return null;
  const tail = ` ${shotLabelText(shot)} 상상도 · AI 생성 이미지로 실제와 다를 수 있음`;
  if (!c.endsWith(tail)) return null;
  const n = c.slice(0, c.length - tail.length).trim();
  return n || null;
}

/** 리졸버 입력 행에서 현장 표시명 — display_name(「 — 」 앞) > name. 없으면 null. */
export function siteNameOf(site: { display_name?: unknown; name?: unknown; [k: string]: unknown } | null | undefined): string | null {
  const d = clean(site?.display_name).split(' — ')[0].trim();
  return d || clean(site?.name) || null;
}

/**
 * 컷 1장의 alt·caption 을 규모 문구로 갈아 끼운다(사실값 없으면 그대로). 이름은 name > 저장 캡션에서 되읽기.
 * 반환은 새 객체(입력 불변).
 */
export function withScaleText<T extends { shot: string; alt: string; caption: string }>(
  shot: T,
  name: string | null | undefined,
  scale: SiteScale | null | undefined,
): T {
  if (!shot || !scale) return shot;
  const n = clean(name) || nameFromStoredCaption(shot.caption, shot.shot);
  const alt = scaleAltText(n, scale, shot.shot);
  const caption = scaleCaptionText(n, scale);
  if (!alt || !caption) return shot;
  return { ...shot, alt, caption };
}

/* ───────────────────────────── site_specs 행 → SiteScale(로더 공용 · 순수) ───────────────────────────── */

/** site_specs 조회 열 — 레인 a 가 detail.scale 에 SiteScale(JSON)을 싣는다(계약 types.ts). 없으면 열 값으로 짠다. */
export const SITE_SPEC_SCALE_COLS = 'site_id,dong_count,max_floor,source,confidence,detail';

export interface SiteSpecScaleRow {
  site_id: string;
  dong_count?: number | null;
  max_floor?: number | null;
  source?: string | null;
  confidence?: string | null;
  detail?: Record<string, unknown> | null;
}

/** 종전(VX-1 fillSiteSpecs) 출처 → 신뢰도. text_regex(설명·블로그 본문 정규식)는 저신뢰 — 노출 문구에 쓰지 않는다. */
const LEGACY_SRC_CONF: Record<string, ScaleConfidence> = {
  apt_subscriptions: 'high', apt_permits: 'high', bujeonggong_seed: 'medium', text_regex: 'low',
  official: 'high', permit: 'high', stated: 'medium', text: 'low',
};

function confOf(v: unknown): ScaleConfidence {
  const s = String(v ?? '').trim().toLowerCase();
  if (s === 'high' || s === 'medium' || s === 'low') return s;
  if (s === 'mid') return 'medium';
  return LEGACY_SRC_CONF[s] ?? 'low';
}

const TIERS: readonly ScaleTier[] = ['XS', 'S', 'M', 'L', 'XL'];
const BTYPES: readonly SiteBuildingType[] = ['apartment', 'mixed_use', 'officetel', 'urban_small', 'small_redev'];

function factOf<T>(raw: unknown, accept: (v: unknown) => T | null): ScaleFact<T> {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!o) return { value: null, source: null, confidence: 'low', estimated: true };
  const value = accept(o.value);
  const source = o.source == null ? null : String(o.source);
  const estimated = o.estimated !== false || value === null;
  return { value, source, confidence: value === null ? 'low' : confOf(o.confidence), estimated };
}

const numAccept = (v: unknown): number | null => {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) && n > 0 ? n : null;
};

const UNKNOWN_FACT: ScaleFact<never> = { value: null, source: null, confidence: 'low', estimated: true };
const order: Record<ScaleConfidence, number> = { low: 0, medium: 1, high: 2 };
const minConf = (...c: ScaleConfidence[]) => c.reduce((a, b) => (order[b] < order[a] ? b : a), 'high' as ScaleConfidence);

/** v3 — 단지 총세대를 모르면 'unknown'(XS/S 축소 어휘 금지). 계약 예고(vs1-a)라 ScaleTier 로 캐스팅한다. */
const TIER_UNKNOWN = 'unknown' as unknown as ScaleTier;

function tierOfUnits(units: number | null, type: SiteBuildingType | null): ScaleTier {
  if (units === null) return TIER_UNKNOWN;
  if (type === 'small_redev' || units < 150) return 'XS';
  if (units < 400) return 'S';
  if (units < 1000) return 'M';
  if (units < 2500) return 'L';
  return 'XL';
}

export interface ScaleRowOpts {
  siteId?: string | null;
  /** 단지 총세대 사실값(apt_sites.complex_units). ⛔ apt_sites.total_units 는 넘기지 않는다(공급과 섞임 — v3). */
  complexUnits?: number | null;
  /** 이번 공급 세대(apt_sites.supply_units) — 표기 구분용. */
  supplyUnits?: number | null;
  nowIso?: string;
}

/**
 * site_specs 1행(+ 선택: apt_sites.complex_units · supply_units 사실값) → SiteScale(+ supplyUnits). 순수.
 *   ① detail.scale 이 계약 모양이면 그것(레인 a 정본) — units·supplyUnits 가 비어 있으면 넘겨받은 열 값으로 채운다.
 *   ② 아니면 열(dong_count·max_floor) + detail.dong_source/floor_source 로 값별 신뢰도를 매긴다(text_regex = low).
 *   행이 없고 세대 값도 없으면 null.
 */
export function scaleFromSpecRow(row: SiteSpecScaleRow | null | undefined, opts?: ScaleRowOpts): SiteScaleLike | null {
  const siteId = String(row?.site_id ?? opts?.siteId ?? '').trim().toLowerCase();
  const cu = intIn(opts?.complexUnits, RANGE.units[0], RANGE.units[1]);
  const spu = intIn(opts?.supplyUnits, RANGE.units[0], RANGE.units[1]);
  const complexFact: ScaleFact<number> = cu !== null ? { value: cu, source: 'apt_sites.complex_units', confidence: 'high', estimated: false } : UNKNOWN_FACT;
  const supplyFact: ScaleFact<number> = spu !== null ? { value: spu, source: 'apt_sites.supply_units', confidence: 'high', estimated: false } : UNKNOWN_FACT;
  if (!siteId) return null;
  const detail = row?.detail && typeof row.detail === 'object' ? row.detail : null;
  const sc = detail && typeof detail.scale === 'object' && detail.scale ? (detail.scale as Record<string, unknown>) : null;
  const resolvedAt = opts?.nowIso ?? new Date(0).toISOString();
  if (sc && (sc.units || sc.dong || sc.maxFloor || sc.supplyUnits)) {
    let units = factOf<number>(sc.units, numAccept);
    if (units.value === null && cu !== null) units = complexFact;
    let supplyUnits = factOf<number>(sc.supplyUnits, numAccept);
    if (supplyUnits.value === null && spu !== null) supplyUnits = supplyFact;
    const dong = factOf<number>(sc.dong, numAccept);
    const maxFloor = factOf<number>(sc.maxFloor, numAccept);
    const buildingType = factOf<SiteBuildingType>(sc.buildingType, (v) => (BTYPES as readonly unknown[]).includes(v) ? (v as SiteBuildingType) : null);
    const tier = (TIERS as readonly unknown[]).includes(sc.tier) || sc.tier === 'unknown' ? (sc.tier as ScaleTier) : tierOfUnits(units.value, buildingType.value);
    return {
      siteId, units, dong, maxFloor, supplyUnits,
      typicalFloor: factOf<number>(sc.typicalFloor, numAccept),
      buildingType,
      platAreaM2: factOf<number>(sc.platAreaM2, numAccept),
      tier,
      confidence: minConf(units.confidence, dong.confidence, maxFloor.confidence),
      resolvedAt: typeof sc.resolvedAt === 'string' ? sc.resolvedAt : resolvedAt,
    };
  }
  if (!row && cu === null && spu === null) return null;
  const rowConf = confOf(row?.confidence);
  const colFact = (v: unknown, src: unknown, k: 'dong' | 'maxFloor'): ScaleFact<number> => {
    const value = intIn(v, RANGE[k][0], RANGE[k][1]);
    if (value === null) return UNKNOWN_FACT;
    const s = src == null || src === '' ? null : String(src);
    const confidence = s ? confOf(s) : rowConf;
    return { value, source: s ? `site_specs.${s}` : `site_specs.${String(row?.source ?? 'unknown')}`, confidence, estimated: false };
  };
  const dong = colFact(row?.dong_count, detail?.dong_source, 'dong');
  const maxFloor = colFact(row?.max_floor, detail?.floor_source, 'maxFloor');
  return {
    siteId, units: complexFact, dong, maxFloor, supplyUnits: supplyFact,
    typicalFloor: UNKNOWN_FACT,
    buildingType: UNKNOWN_FACT,
    platAreaM2: UNKNOWN_FACT,
    tier: tierOfUnits(complexFact.value, null),
    confidence: minConf(complexFact.confidence, dong.confidence, maxFloor.confidence),
    resolvedAt,
  };
}

/**
 * 블로그·디자인 시각자료(vx4 facts)용 — site_specs 1행에서 «확인된» 동 수·최고층만. 추정·저신뢰(text_regex 등)는 null.
 */
export function confirmedSpecValues(row: SiteSpecScaleRow | null | undefined): { dong: number | null; maxFloor: number | null } {
  if (!row) return { dong: null, maxFloor: null };
  const f = confirmedScaleFacts(scaleFromSpecRow(row, { siteId: row.site_id || 'x' }));
  return { dong: f.dong, maxFloor: f.maxFloor };
}

/* ───────────────────────────── 하류 판정(리졸버 결과를 문자열로만 받는 자리 — 허브 카드·대표 현장) ───────────────────────────── */

const SCALE_TEXT_RE = /^\S.* 기준 AI 상상도(\((주간 항공|노을 항공|단지 입구|야경)\))? — 실제와 다를 수 있음$/;

/** 이 alt·caption 이 `scaleAltText`/`scaleCaptionText` 의 결과인가(DB alt_ko·caption_ko 꼴과 겹치지 않는다). */
export function isScaleText(s: string | null | undefined): boolean {
  return SCALE_TEXT_RE.test(clean(s));
}

/** 규모 alt(컷 괄호 포함) → 같은 현장의 규모 캡션(괄호 제거). 규모 문구가 아니면 null. */
export function scaleCaptionOfAlt(alt: string | null | undefined): string | null {
  const a = clean(alt);
  if (!isScaleText(a)) return null;
  return a.replace(/ 기준 AI 상상도\([^)]*\) — /, ' 기준 AI 상상도 — ');
}

/**
 * 고지 꼬리 없는 라벨 — 「{단지명} {사실} 기준 AI 상상도」. AI 고지(AI_NOTICE)를 따로 1회 내는 자리(카페 본문 등)용 —
 *   content-gates G7(ai_notice_count: /AI…실제와 다를 수 있/ 정확히 1회)를 캡션이 한 번 더 세지 않게 한다. 조건은 캡션과 같다.
 */
export function scaleLabelText(name: string | null | undefined, scale: SiteScale | null | undefined): string | null {
  const c = scaleCaptionText(name, scale);
  return c ? c.slice(0, c.length - ' — 실제와 다를 수 있음'.length) : null;
}
